import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, BarChart3, Trophy, TrendingDown } from 'lucide-react';
import config from '../config';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les tops et flops produits
 * Top produits = 5 produits les plus vendus
 * Flop produits = 5 produits les plus cliqués sans vente
 *
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 */
const TopFlopProductsTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  /**
   * Les campagnes retenues par les filtres par identifiant — labels et type.
   *
   * `campaignTypeFilter` est envoyé à la route mais n'y produit rien : elle travaille sur des
   * produits agrégés, qui ne portent que l'identifiant de leur campagne, pas son type. Le
   * dashboard traduit donc le filtre en liste d'identifiants, seul langage que cette route
   * sache appliquer.
   */
  campaignIds = null,
  token,
  setError,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'topflop_products',
  loadingComparison = false
}) => {
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [showMetricsConfigTop, setShowMetricsConfigTop] = useState(false);
  const [showMetricsConfigFlop, setShowMetricsConfigFlop] = useState(false);

  // Configuration des métriques séparées pour Top et Flop
  const topMetrics = useComponentMetrics('topflop_products_top', selectedAccount?.id || 'global');
  const flopMetrics = useComponentMetrics('topflop_products_flop', selectedAccount?.id || 'global');

  // Fonctions de visibilité des métriques pour Top et Flop
  const isTopMetricVisible = useCallback((metricKey) => {
    return topMetrics.metrics.includes(metricKey);
  }, [topMetrics.metrics]);

  const isFlopMetricVisible = useCallback((metricKey) => {
    return flopMetrics.metrics.includes(metricKey);
  }, [flopMetrics.metrics]);

  // États locaux pour les données
  const [topProducts, setTopProducts] = useState([]);
  const [flopProducts, setFlopProducts] = useState([]);
  const [previousTopProducts, setPreviousTopProducts] = useState([]);
  const [previousFlopProducts, setPreviousFlopProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);





  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    // Ne pas charger les données si le module est masqué
    if (!isVisible) return;

    console.log('📊 [TOP-FLOP-PRODUCTS] useEffect déclenché:', {
      selectedAccount: selectedAccount?.id,
      selectedPeriod,
      hasToken: !!token,
      hasPreloadedData: !!preloadedData,
      compareEnabled,
      comparisonType
    });

    // Fonction pour charger les données de la période précédente (définie en premier pour éviter les erreurs de référence)
    const loadPreviousPeriodData = async () => {
      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      console.log(`📊 [TOP-FLOP-PRODUCTS] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      setLoadingPreviousPeriod(true);
      try {
        const prevResponse = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-products`,
          {
            sources: [selectedAccount.id],
            period: 'CUSTOM',
            customStartDate: prevPeriod.startDate,
            customEndDate: prevPeriod.endDate,
            campaignFilter: selectedCampaignFilter || null,
            campaignTypeFilter: selectedCampaignTypeFilter || null,
            campaignIds: campaignIds && campaignIds.length > 0 ? campaignIds : null,
            limit: 5
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (prevResponse.data.success) {
          console.log(`📊 [TOP-FLOP-PRODUCTS] ${prevResponse.data.data.topProducts?.length || 0} top produits, ${prevResponse.data.data.flopProducts?.length || 0} flop produits période précédente`);
          setPreviousTopProducts(prevResponse.data.data.topProducts || []);
          setPreviousFlopProducts(prevResponse.data.data.flopProducts || []);
        }
      } catch (prevError) {
        console.warn(`⚠️ [TOP-FLOP-PRODUCTS] Erreur chargement période précédente:`, prevError.message);
        setPreviousTopProducts([]);
        setPreviousFlopProducts([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    // Si des données préchargées sont disponibles ET qu'aucun filtre n'est actif
    // `campaignIds` dans la garde : les données préchargées portent tout le compte. S'en servir
    // alors qu'un filtre par label ou par type est posé afficherait les produits du compte entier
    // sous un en-tête filtré — le défaut même qu'on corrige ici.
    const sansFiltreCampagnes = !campaignIds || campaignIds.length === 0;
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && sansFiltreCampagnes) {
      const hasPreloadedProducts = (preloadedData.data?.topProducts?.length > 0) || (preloadedData.data?.flopProducts?.length > 0);

      console.log('📊 [TOP-FLOP-PRODUCTS] Vérification données préchargées:', {
        topProductsCount: preloadedData.data?.topProducts?.length || 0,
        flopProductsCount: preloadedData.data?.flopProducts?.length || 0,
        hasPreloadedProducts
      });

      if (hasPreloadedProducts) {
        // Utiliser les données préchargées seulement si elles contiennent des produits
        setTopProducts(preloadedData.data.topProducts || []);
        setFlopProducts(preloadedData.data.flopProducts || []);
        setIsDataLoaded(true);
        setLoading(false);

        // Si comparaison activée, charger les données de la période précédente
        if (compareEnabled && comparisonType) {
          loadPreviousPeriodData();
        } else {
          setPreviousTopProducts([]);
          setPreviousFlopProducts([]);
        }
        return;
      }
      // Sinon, continuer pour charger les données depuis l'API
      console.log('📊 [TOP-FLOP-PRODUCTS] PreloadedData vide, chargement depuis API...');
    }

    if (!selectedAccount || !selectedPeriod || !token) {
      console.log('🔍 [TOP-FLOP-PRODUCTS-MODULAR] Paramètres manquants:', {
        selectedAccount: !!selectedAccount,
        selectedPeriod: !!selectedPeriod,
        token: !!token
      });
      return;
    }

    const loadTopFlopProducts = async () => {
      try {
        setLoading(true);

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          campaignIds: campaignIds && campaignIds.length > 0 ? campaignIds : null,
          limit: 5
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }


        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-products`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );


        if (response.data.success) {
          setTopProducts(response.data.data.topProducts || []);
          setFlopProducts(response.data.data.flopProducts || []);
          setIsDataLoaded(true);

          // Charger les données de la période précédente si comparaison activée
          if (compareEnabled && comparisonType) {
            await loadPreviousPeriodData();
          } else {
            setPreviousTopProducts([]);
            setPreviousFlopProducts([]);
          }
        } else {
          console.error('❌ [TOP-FLOP-PRODUCTS-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des tops/flops produits');
        }
      } catch (error) {
        console.error('❌ [TOP-FLOP-PRODUCTS-MODULAR] Erreur chargement:', error);
        console.error('❌ [TOP-FLOP-PRODUCTS-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des tops/flops produits');
      } finally {
        setLoading(false);
      }
    };

    loadTopFlopProducts();
  // `campaignIds` dans les dépendances : sans elles, changer de filtre par type ou par label
  // ne relancerait pas la requête, et le tableau garderait les produits du filtre précédent.
  }, [isVisible, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, campaignIds, token, setError, preloadedData, compareEnabled, comparisonType]);

  // Filtrer les produits par labels
  const filteredTopProducts = useMemo(() => {
    if (selectedLabelsFilter.length === 0) {
      return topProducts;
    }
    return topProducts.filter(product => {
      const productCampaignId = product.campaignId || product.campaign_id;
      return campaignMatchesLabelsFilter(productCampaignId);
    });
  }, [topProducts, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  const filteredFlopProducts = useMemo(() => {
    if (selectedLabelsFilter.length === 0) {
      return flopProducts;
    }
    return flopProducts.filter(product => {
      const productCampaignId = product.campaignId || product.campaign_id;
      return campaignMatchesLabelsFilter(productCampaignId);
    });
  }, [flopProducts, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Fonction pour trouver le produit correspondant dans les données précédentes
  const findPreviousProduct = useCallback((product, previousProducts) => {
    if (!compareEnabled || !previousProducts || previousProducts.length === 0) return null;

    // Chercher par productId d'abord
    if (product.productId) {
      const found = previousProducts.find(p => p.productId === product.productId);
      if (found) return found;
    }

    // Sinon chercher par title
    if (product.title) {
      const found = previousProducts.find(p => p.title === product.title);
      if (found) return found;
    }

    return null;
  }, [compareEnabled]);

  // Calcul des totaux pour Top Products
  const topProductsTotals = useMemo(() => {
    if (!filteredTopProducts.length) return null;

    const baseTotals = filteredTopProducts.reduce((acc, product) => ({
      impressions: acc.impressions + (product.impressions || 0),
      clicks: acc.clicks + (product.clicks || 0),
      conversions: acc.conversions + (product.conversions || 0),
      cost: acc.cost + (product.cost || 0),
      conversionsValue: acc.conversionsValue + (product.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredTopProducts]);

  // Calcul des totaux précédents pour Top Products
  const previousTopProductsTotals = useMemo(() => {
    if (!compareEnabled || !previousTopProducts.length) return null;

    const baseTotals = previousTopProducts.reduce((acc, product) => ({
      impressions: acc.impressions + (product.impressions || 0),
      clicks: acc.clicks + (product.clicks || 0),
      conversions: acc.conversions + (product.conversions || 0),
      cost: acc.cost + (product.cost || 0),
      conversionsValue: acc.conversionsValue + (product.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousTopProducts]);

  // Calcul des totaux pour Flop Products
  const flopProductsTotals = useMemo(() => {
    if (!filteredFlopProducts.length) return null;

    const baseTotals = filteredFlopProducts.reduce((acc, product) => ({
      impressions: acc.impressions + (product.impressions || 0),
      clicks: acc.clicks + (product.clicks || 0),
      cost: acc.cost + (product.cost || 0),
      conversions: acc.conversions + (product.conversions || 0),
      conversionsValue: acc.conversionsValue + (product.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredFlopProducts]);

  // Calcul des totaux précédents pour Flop Products
  const previousFlopProductsTotals = useMemo(() => {
    if (!compareEnabled || !previousFlopProducts.length) return null;

    const baseTotals = previousFlopProducts.reduce((acc, product) => ({
      impressions: acc.impressions + (product.impressions || 0),
      clicks: acc.clicks + (product.clicks || 0),
      cost: acc.cost + (product.cost || 0),
      conversions: acc.conversions + (product.conversions || 0),
      conversionsValue: acc.conversionsValue + (product.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousFlopProducts]);

  // Debug des conditions d'affichage
  console.log('📊 [TOP-FLOP-PRODUCTS] État:', {
    loading,
    isDataLoaded,
    topProductsCount: topProducts.length,
    flopProductsCount: flopProducts.length,
    previousTopProductsCount: previousTopProducts.length,
    previousFlopProductsCount: previousFlopProducts.length,
    selectedAccount: selectedAccount?.id,
    selectedPeriod,
    hasToken: !!token,
    compareEnabled
  });

  // Attendre que les paramètres nécessaires soient disponibles
  if (!selectedAccount || !token) {
    console.log('📊 [TOP-FLOP-PRODUCTS] En attente des paramètres (compte/token)');
    return null;
  }

  // Ne pas afficher si pas de données et pas en chargement (mais seulement si on a essayé de charger)
  if (!loading && !isDataLoaded && !preloadedData) {
    console.log('📊 [TOP-FLOP-PRODUCTS] Masqué: pas de données et pas en chargement');
    return null;
  }

  // Masquer complètement si aucun produit trouvé (après filtrage par labels)
  const hasNoProducts = !loading && isDataLoaded && filteredTopProducts.length === 0 && filteredFlopProducts.length === 0;

  if (hasNoProducts) {
    console.log('📊 [TOP-FLOP-PRODUCTS] Masqué: aucun produit trouvé');
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart3 size={16} />&nbsp;Top & Flop Produits</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Top/Flop Produits"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <div className="table-container" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des tops & flops produits...<br /><br />
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : (
            <div className="paire-top-flop" style={{ display: 'flex', gap: '20px' }}>
              {/* Top Produits */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#2e7d32', fontSize: '1.1em' }}>
                    <Trophy size={16} />&nbsp;Top 5 Produits (Plus vendus)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigTop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Produit</th>
                        {isTopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isTopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isTopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isTopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isTopMetricVisible('cout') && <th className="numeric">Cout</th>}
                        {isTopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isTopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isTopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isTopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isTopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isTopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isTopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isTopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTopProducts.length === 0 ? (
                        <tr>
                          <td colSpan={1 + (isTopMetricVisible('impressions') ? 1 : 0) + (isTopMetricVisible('clics') ? 1 : 0) + (isTopMetricVisible('ctr') ? 1 : 0) + (isTopMetricVisible('cpc') ? 1 : 0) + (isTopMetricVisible('cout') ? 1 : 0) + (isTopMetricVisible('taux_conv') ? 1 : 0) + (isTopMetricVisible('conv') ? 1 : 0) + (isTopMetricVisible('cpa') ? 1 : 0) + (isTopMetricVisible('valeur_conv') ? 1 : 0) + (isTopMetricVisible('cos') ? 1 : 0) + (isTopMetricVisible('panier_moyen') ? 1 : 0) + (isTopMetricVisible('roas') ? 1 : 0) + (isTopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        filteredTopProducts.map((product, index) => {
                          const prevProduct = findPreviousProduct(product, previousTopProducts);
                          const hasComparison = !!prevProduct;
                          const prevConversionRate = prevProduct && prevProduct.clicks > 0 ? (prevProduct.conversions / prevProduct.clicks * 100) : 0;
                          const prevCpa = prevProduct && prevProduct.conversions > 0 ? (prevProduct.cost / prevProduct.conversions) : 0;
                          const prevCos = prevProduct && prevProduct.conversionsValue > 0 ? (prevProduct.cost / prevProduct.conversionsValue * 100) : 0;

                          const ctr = product.impressions > 0 ? (product.clicks / product.impressions * 100) : 0;
                          const cpc = product.clicks > 0 ? (product.cost / product.clicks) : 0;
                          const roas = product.cost > 0 ? (product.conversionsValue / product.cost) : 0;
                          const roi = (product.cost > 0 && product.conversionsValue > 0) ? ((product.conversionsValue - product.cost) / product.cost * 100) : 0;
                          const prevCtr = prevProduct && prevProduct.impressions > 0 ? (prevProduct.clicks / prevProduct.impressions * 100) : 0;
                          const prevCpc = prevProduct && prevProduct.clicks > 0 ? (prevProduct.cost / prevProduct.clicks) : 0;
                          const prevRoas = prevProduct && prevProduct.cost > 0 ? (prevProduct.conversionsValue / prevProduct.cost) : 0;
                          const prevRoi = prevProduct && prevProduct.cost > 0 && prevProduct.conversionsValue > 0 ? ((prevProduct.conversionsValue - prevProduct.cost) / prevProduct.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {product.title || 'Produit non spécifié'}
                              </td>
                              {isTopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(product.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(product.impressions || 0, prevProduct?.impressions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(product.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(product.clicks || 0, prevProduct.clicks || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isTopMetricVisible('cpc') && (
                                <td className="numeric">
                                  {formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isTopMetricVisible('cout') && (
                                <td className="numeric">
                                  {formatNumber(product.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(product.cost || 0, prevProduct?.cost || 0), true)}
                                </td>
                              )}
                              {isTopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(product.conversionRate || 0, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(product.conversionRate || 0, prevConversionRate))}
                                </td>
                              )}
                              {isTopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(product.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(product.conversions || 0, prevProduct.conversions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(product.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {product.conversions > 0 ? `${formatNumber(product.cpa || 0, 2)}` : ''}
                                  {hasComparison && renderVariation(calculateVariation(product.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isTopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  {formatNumber(product.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(product.conversionsValue || 0, prevProduct?.conversionsValue || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(product.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(product.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(product.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isTopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  {formatNumber(product.conversions > 0 ? (product.conversionsValue || 0) / product.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    product.conversions > 0 ? (product.conversionsValue || 0) / product.conversions : 0,
                                    (prevProduct?.conversionsValue || 0) / prevProduct.conversions
                                  ))}
                                </td>
                              )}
                              {isTopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isTopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {filteredTopProducts.length > 0 && topProductsTotals && (() => {
                      const totalCtr = topProductsTotals.impressions > 0 ? (topProductsTotals.clicks / topProductsTotals.impressions * 100) : 0;
                      const totalCpc = topProductsTotals.clicks > 0 ? (topProductsTotals.cost / topProductsTotals.clicks) : 0;
                      const totalRoas = topProductsTotals.cost > 0 ? (topProductsTotals.conversionsValue / topProductsTotals.cost) : 0;
                      const totalRoi = topProductsTotals.cost > 0 ? ((topProductsTotals.conversionsValue - topProductsTotals.cost) / topProductsTotals.cost * 100) : 0;
                      const prevCtr = previousTopProductsTotals && previousTopProductsTotals.impressions > 0 ? (previousTopProductsTotals.clicks / previousTopProductsTotals.impressions * 100) : 0;
                      const prevCpc = previousTopProductsTotals && previousTopProductsTotals.clicks > 0 ? (previousTopProductsTotals.cost / previousTopProductsTotals.clicks) : 0;
                      const prevRoas = previousTopProductsTotals && previousTopProductsTotals.cost > 0 ? (previousTopProductsTotals.conversionsValue / previousTopProductsTotals.cost) : 0;
                      const prevRoi = previousTopProductsTotals && previousTopProductsTotals.cost > 0 ? ((previousTopProductsTotals.conversionsValue - previousTopProductsTotals.cost) / previousTopProductsTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isTopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.impressions)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.impressions, previousTopProductsTotals.impressions))}
                            </td>
                          )}
                          {isTopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.clicks)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.clicks, previousTopProductsTotals.clicks))}
                            </td>
                          )}
                          {isTopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isTopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCpc, 2)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(totalCpc, prevCpc), true)}
                            </td>
                          )}
                          {isTopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.cost, 2)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.cost, previousTopProductsTotals.cost), true)}
                            </td>
                          )}
                          {isTopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.conversionRate, 2)}%</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.conversionRate, previousTopProductsTotals.conversionRate))}
                            </td>
                          )}
                          {isTopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.conversions, 1)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.conversions, previousTopProductsTotals.conversions))}
                            </td>
                          )}
                          {isTopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(topProductsTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{topProductsTotals.conversions > 0 ? `${formatNumber(topProductsTotals.cpa, 2)}` : ''}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.cpa, previousTopProductsTotals.cpa), true)}
                            </td>
                          )}
                          {isTopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.conversionsValue, 2)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.conversionsValue, previousTopProductsTotals.conversionsValue))}
                            </td>
                          )}
                          {isTopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(topProductsTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(topProductsTotals.cos, 1)}%</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(topProductsTotals.cos, previousTopProductsTotals.cos), true)}
                            </td>
                          )}
                          {isTopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>{formatNumber(topProductsTotals.conversions > 0 ? topProductsTotals.conversionsValue / topProductsTotals.conversions : 0, 2)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(
                                topProductsTotals.conversions > 0 ? topProductsTotals.conversionsValue / topProductsTotals.conversions : 0,
                                previousTopProductsTotals.conversionsValue / previousTopProductsTotals.conversions
                              ))}
                            </td>
                          )}
                          {isTopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isTopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousTopProductsTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>

              {/* Flop Produits */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#d32f2f', fontSize: '1.1em' }}>
                    <TrendingDown size={16} />&nbsp;Flop 5 Produits (Plus cliqués sans vente)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigFlop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Produit</th>
                        {isFlopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isFlopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isFlopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isFlopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isFlopMetricVisible('cout') && <th className="numeric">Cout</th>}
                        {isFlopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isFlopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isFlopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isFlopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isFlopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isFlopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isFlopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isFlopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFlopProducts.length === 0 ? (
                        <tr>
                          <td colSpan={1 + (isFlopMetricVisible('impressions') ? 1 : 0) + (isFlopMetricVisible('clics') ? 1 : 0) + (isFlopMetricVisible('ctr') ? 1 : 0) + (isFlopMetricVisible('cpc') ? 1 : 0) + (isFlopMetricVisible('cout') ? 1 : 0) + (isFlopMetricVisible('taux_conv') ? 1 : 0) + (isFlopMetricVisible('conv') ? 1 : 0) + (isFlopMetricVisible('cpa') ? 1 : 0) + (isFlopMetricVisible('valeur_conv') ? 1 : 0) + (isFlopMetricVisible('cos') ? 1 : 0) + (isFlopMetricVisible('panier_moyen') ? 1 : 0) + (isFlopMetricVisible('roas') ? 1 : 0) + (isFlopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        filteredFlopProducts.map((product, index) => {
                          const prevProduct = findPreviousProduct(product, previousFlopProducts);
                          const hasComparison = !!prevProduct;
                          const ctr = product.impressions > 0 ? (product.clicks / product.impressions * 100) : 0;
                          const cpc = product.clicks > 0 ? (product.cost / product.clicks) : 0;
                          const convRate = product.clicks > 0 ? (product.conversions / product.clicks * 100) : 0;
                          const roas = product.cost > 0 ? (product.conversionsValue / product.cost) : 0;
                          const roi = (product.cost > 0 && product.conversionsValue > 0) ? ((product.conversionsValue - product.cost) / product.cost * 100) : 0;
                          const prevCtr = prevProduct && prevProduct.impressions > 0 ? (prevProduct.clicks / prevProduct.impressions * 100) : 0;
                          const prevCpc = prevProduct && prevProduct.clicks > 0 ? (prevProduct.cost / prevProduct.clicks) : 0;
                          const prevConvRate = prevProduct && prevProduct.clicks > 0 ? (prevProduct.conversions / prevProduct.clicks * 100) : 0;
                          const prevCpa = prevProduct && prevProduct.conversions > 0 ? (prevProduct.cost / prevProduct.conversions) : 0;
                          const prevCos = prevProduct && prevProduct.conversionsValue > 0 ? (prevProduct.cost / prevProduct.conversionsValue * 100) : 0;
                          const prevRoas = prevProduct && prevProduct.cost > 0 ? (prevProduct.conversionsValue / prevProduct.cost) : 0;
                          const prevRoi = prevProduct && prevProduct.cost > 0 && prevProduct.conversionsValue > 0 ? ((prevProduct.conversionsValue - prevProduct.cost) / prevProduct.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {product.title || 'Produit non spécifié'}
                              </td>
                              {isFlopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(product.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(product.impressions || 0, prevProduct?.impressions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(product.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(product.clicks || 0, prevProduct.clicks || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpc') && (
                                <td className="numeric">
                                  {formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('cout') && (
                                <td className="numeric">
                                  {formatNumber(product.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(product.cost || 0, prevProduct?.cost || 0), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(convRate, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(convRate, prevConvRate))}
                                </td>
                              )}
                              {isFlopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(product.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(product.conversions || 0, prevProduct?.conversions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(product.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {product.conversions > 0 ? `${formatNumber(product.cpa || 0, 2)}` : ''}
                                  {hasComparison && renderVariation(calculateVariation(product.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  {formatNumber(product.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(product.conversionsValue || 0, prevProduct?.conversionsValue || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(product.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(product.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(product.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  {formatNumber(product.conversions > 0 ? (product.conversionsValue || 0) / product.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    product.conversions > 0 ? (product.conversionsValue || 0) / product.conversions : 0,
                                    (prevProduct?.conversionsValue || 0) / prevProduct.conversions
                                  ))}
                                </td>
                              )}
                              {isFlopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isFlopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {filteredFlopProducts.length > 0 && flopProductsTotals && (() => {
                      const totalCtr = flopProductsTotals.impressions > 0 ? (flopProductsTotals.clicks / flopProductsTotals.impressions * 100) : 0;
                      const totalConvRate = flopProductsTotals.clicks > 0 ? (flopProductsTotals.conversions / flopProductsTotals.clicks * 100) : 0;
                      const totalRoas = flopProductsTotals.cost > 0 ? (flopProductsTotals.conversionsValue / flopProductsTotals.cost) : 0;
                      const totalRoi = flopProductsTotals.cost > 0 ? ((flopProductsTotals.conversionsValue - flopProductsTotals.cost) / flopProductsTotals.cost * 100) : 0;
                      const prevCtr = previousFlopProductsTotals && previousFlopProductsTotals.impressions > 0 ? (previousFlopProductsTotals.clicks / previousFlopProductsTotals.impressions * 100) : 0;
                      const prevConvRate = previousFlopProductsTotals && previousFlopProductsTotals.clicks > 0 ? (previousFlopProductsTotals.conversions / previousFlopProductsTotals.clicks * 100) : 0;
                      const prevRoas = previousFlopProductsTotals && previousFlopProductsTotals.cost > 0 ? (previousFlopProductsTotals.conversionsValue / previousFlopProductsTotals.cost) : 0;
                      const prevRoi = previousFlopProductsTotals && previousFlopProductsTotals.cost > 0 ? ((previousFlopProductsTotals.conversionsValue - previousFlopProductsTotals.cost) / previousFlopProductsTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isFlopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.impressions)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.impressions, previousFlopProductsTotals.impressions))}
                            </td>
                          )}
                          {isFlopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.clicks)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.clicks, previousFlopProductsTotals.clicks))}
                            </td>
                          )}
                          {isFlopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.cpc, 2)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.cpc, previousFlopProductsTotals.cpc), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.cost, 2)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.cost, previousFlopProductsTotals.cost), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalConvRate, 2)}%</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(totalConvRate, prevConvRate))}
                            </td>
                          )}
                          {isFlopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.conversions, 1)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.conversions, previousFlopProductsTotals.conversions))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(flopProductsTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{flopProductsTotals.conversions > 0 ? `${formatNumber(flopProductsTotals.cpa, 2)}` : ''}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.cpa, previousFlopProductsTotals.cpa), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.conversionsValue, 2)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.conversionsValue, previousFlopProductsTotals.conversionsValue))}
                            </td>
                          )}
                          {isFlopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(flopProductsTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(flopProductsTotals.cos, 1)}%</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(flopProductsTotals.cos, previousFlopProductsTotals.cos), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopProductsTotals.conversions > 0 ? flopProductsTotals.conversionsValue / flopProductsTotals.conversions : 0, 2)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(
                                flopProductsTotals.conversions > 0 ? flopProductsTotals.conversionsValue / flopProductsTotals.conversions : 0,
                                previousFlopProductsTotals.conversionsValue / previousFlopProductsTotals.conversions
                              ))}
                            </td>
                          )}
                          {isFlopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isFlopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousFlopProductsTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques pour Top */}
      <MetricsConfigModal
        isOpen={showMetricsConfigTop}
        onClose={() => setShowMetricsConfigTop(false)}
        componentId="topflop_products_top"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Top 5 Produits (Plus vendus)"
        hideApplyToAll={true}
      />

      {/* Modal de configuration des métriques pour Flop */}
      <MetricsConfigModal
        isOpen={showMetricsConfigFlop}
        onClose={() => setShowMetricsConfigFlop(false)}
        componentId="topflop_products_flop"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Flop 5 Produits (Plus cliqués sans vente)"
        hideApplyToAll={true}
      />
    </div>
  );
});

TopFlopProductsTableModular.displayName = 'TopFlopProductsTableModular';

export default TopFlopProductsTableModular;
